/**
 * Vocabulário de permissões de um agent-bot.
 *
 * A lista NÃO é inventada aqui: é a mesma do backend —
 * `CustomRole::PERMISSIONS` (app/models/custom_role.rb), já espelhada no
 * frontend em `AVAILABLE_CUSTOM_ROLE_PERMISSIONS`, mais
 * `AgentBot::EXTRA_PERMISSIONS` (app/models/agent_bot.rb), que são as
 * permissões sem equivalente humano. Qualquer valor fora dessa união volta
 * 422 do servidor ("unknown bot permissions: ...").
 *
 * O agrupamento existe só para a tela: o que viaja para a API continua sendo
 * a lista plana em `bot_config.permissions`.
 */
import { AVAILABLE_CUSTOM_ROLE_PERMISSIONS } from 'dashboard/constants/permissions';

// Espelha AgentBot::EXTRA_PERMISSIONS.
export const AGENT_BOT_EXTRA_PERMISSIONS = ['conversation_private_notes_view'];

// Espelha AgentBot.known_permissions.
export const AGENT_BOT_KNOWN_PERMISSIONS = [
  ...AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  ...AGENT_BOT_EXTRA_PERMISSIONS,
];

/**
 * Domínios da tela. `id` vira a chave de i18n do grupo e `value` a chave do
 * item — ambas em AGENT_BOTS.FORM.PERMISSIONS.
 *
 * `sensitive` marca a permissão que precisa de aviso próprio: quem a liga
 * está entregando ao robô o que a equipe escreve em particular.
 */
export const AGENT_BOT_PERMISSION_GROUPS = [
  {
    id: 'SUPPORT',
    permissions: [
      { value: 'conversation_manage' },
      { value: 'conversation_unassigned_manage' },
      { value: 'conversation_participating_manage' },
      { value: 'conversation_awaiting_reply_view' },
      { value: 'contact_manage' },
      { value: 'conversation_private_notes_view', sensitive: true },
    ],
  },
  {
    id: 'CRM',
    permissions: [{ value: 'pipeline_manage' }, { value: 'pipeline_view' }],
  },
  {
    id: 'SCHEDULING',
    permissions: [{ value: 'appointment_manage' }],
  },
  {
    id: 'FOLLOW_UP',
    permissions: [{ value: 'follow_up_manage' }],
  },
  {
    id: 'INSIGHTS',
    permissions: [
      { value: 'report_manage' },
      { value: 'commercial_analysis_manage' },
      { value: 'knowledge_base_manage' },
    ],
  },
];

// Ordem canônica do catálogo: usada para normalizar a lista salva, para o
// diff no servidor não depender da ordem em que a pessoa clicou.
export const AGENT_BOT_PERMISSIONS_IN_GROUP_ORDER =
  AGENT_BOT_PERMISSION_GROUPS.flatMap(group =>
    group.permissions.map(({ value }) => value)
  );

export const permissionTranslationKey = permission =>
  String(permission).toUpperCase();

/**
 * Descarta o que o backend recusaria e devolve na ordem do catálogo.
 * Lista vazia é resposta legítima — bot sem permissão é o padrão.
 */
/**
 * ORDENA, não filtra. A tela não decide o que é uma permissão válida — o backend
 * decide, e recusa com 422 o que não conhece.
 *
 * Duas versões anteriores deste helper descartavam em silêncio: a primeira o que
 * não tinha grupo na UI, a segunda o que não estava no vocabulário embutido no
 * front. As duas produzem o mesmo dano, e é um dano invisível: o formulário envia
 * a lista inteira ao salvar, então bastava um administrador conceder por API uma
 * permissão que aquele front não conhecia — ou o backend estar uma versão à
 * frente, o que é o estado normal durante uma subida — e abrir a tela para salvar
 * outra coisa. A permissão sumia sem erro, sem aviso e sem aparecer no que ele viu.
 *
 * O que sobra aqui é apresentação: as conhecidas primeiro, na ordem dos grupos,
 * para a lista ficar legível; o resto preservado no fim, na ordem em que veio.
 * Duplicatas caem, porque enviar duas vezes a mesma permissão não significa nada.
 */
export const sanitizeAgentBotPermissions = (permissions = []) => {
  const recebidas = (Array.isArray(permissions) ? permissions : []).map(String);
  const pedidas = new Set(recebidas);

  const conhecidas = AGENT_BOT_PERMISSIONS_IN_GROUP_ORDER.filter(permission =>
    pedidas.has(permission)
  );
  const desconhecidas = recebidas.filter(
    (permission, indice) =>
      !AGENT_BOT_PERMISSIONS_IN_GROUP_ORDER.includes(permission) &&
      recebidas.indexOf(permission) === indice
  );

  return [...conhecidas, ...desconhecidas];
};

// AÇÕES GRANULARES dentro de `pipeline_manage`. Espelha AgentBot::PIPELINE_ACTIONS
// (app/models/agent_bot.rb) — vocabulário FECHADO, e o backend valida: ação
// desconhecida vira 422, não autoridade silenciosa.
//
// Semântica: lista VAZIA = tudo que `pipeline_manage` já concede. A lista
// ESTREITA, não é requisito. Mesmo padrão do estreitamento por funil.
export const AGENT_BOT_PIPELINE_ACTIONS = [
  'cards_move',
  'cards_win',
  'cards_lose',
  'cards_edit',
  'cards_assign',
  'pipelines_manage',
];

export const pipelineActionTranslationKey = action =>
  `AGENT_BOTS.FORM.PIPELINE_ACTIONS.ITEMS.${action}`;

export const sanitizeAgentBotPipelineActions = (actions = []) =>
  Array.isArray(actions)
    ? [
        ...new Set(
          actions
            .map(a => String(a || '').trim())
            .filter(a => AGENT_BOT_PIPELINE_ACTIONS.includes(a))
        ),
      ]
    : [];
